// src/components/TodaysSpecial.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './TodaysSpecial.css';
import leafImage        from '../assets/images/leaf.png';
import veganIcon        from '../assets/images/dietary/vegan.png';
import pescatarianIcon  from '../assets/images/dietary/Pescatarian.png';
import halalIcon        from '../assets/images/dietary/Halal.png';
import vegIcon          from '../assets/images/dietary/veg.png';
import nonVegIcon       from '../assets/images/dietary/Non-veg.png';

const DIETARY_ICONS = {
  vegan:          { icon: veganIcon,       label: 'Vegan' },
  vegetarian:     { icon: vegIcon,         label: 'Veg' },
  pescatarian:    { icon: pescatarianIcon, label: 'Pescatarian' },
  halal:          { icon: halalIcon,       label: 'Halal' },
  non_vegetarian: { icon: nonVegIcon,      label: 'Non-Veg' },
};

const TodaysSpecial = ({ tenant = {}, onAddToCart }) => {
  const [specialData, setSpecialData]                   = useState(null);
  const [specialItems, setSpecialItems]                 = useState([]);
  const [loading, setLoading]                           = useState(true);
  const [showAll, setShowAll]                           = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [addingItemId, setAddingItemId]                 = useState(null);
  const [addedItemId, setAddedItemId]                   = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }

    fetchSpecialData();
    fetchSpecialItems();

    const sectionChannel = supabase
      .channel('special-section-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'website_sections',
        filter: `tenant_id=eq.${tenant.id}`,
      }, (payload) => {
        if (payload.new?.section_name === 'special') {
          setSpecialData(payload.new.content);
        }
      }).subscribe();

    const itemsChannel = supabase
      .channel('special-items-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'menu_items',
        filter: `tenant_id=eq.${tenant.id}`,
      }, () => fetchSpecialItems())
      .subscribe();

    return () => {
      supabase.removeChannel(sectionChannel);
      supabase.removeChannel(itemsChannel);
    };
  }, [tenant?.id]);

  const fetchSpecialData = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sections')
        .select('content, is_published')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'special')
        .maybeSingle();

      if (error) {
        setSpecialData(getDefaultData());
      } else if (data && data.is_published) {
        setSpecialData(data.content);
      } else {
        setSpecialData(getDefaultData());
      }
    } catch {
      setSpecialData(getDefaultData());
    }
  };

  const fetchSpecialItems = async () => {
    try {
      setLoading(true);
      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_items')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_featured', true)
        .eq('is_available', true)
        .is('deleted_at', null)
        .order('display_order', { ascending: true });

      if (itemsError) throw itemsError;
      setSpecialItems(itemsData || []);
    } catch (error) {
      console.error('Error loading special items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultData = () => ({
    heading: "Today's Special",
    subheading: 'Special Menu',
    description: 'Special menu often comes different everyday, this is our special food for today',
    is_active: true,
  });

  const toggleDescription = (itemId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const calculateDiscount = (originalPrice, currentPrice) => {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  const primaryColor = tenant?.primary_color || '#FF9F0D';
  const currency     = tenant?.currency || 'INR';

  const formatPrice = (price) => {
    if (currency === 'INR') return `₹${price}`;
    if (currency === 'USD') return `$${price}`;
    if (currency === 'EUR') return `€${price}`;
    return `${currency} ${price}`;
  };

  // ✅ Add to cart — no auto-open
  const handleAddToCart = async (e, item) => {
    e.stopPropagation();
    if (!onAddToCart || addingItemId === item.id) return;

    setAddingItemId(item.id);
    await onAddToCart(item, 1);
    setAddingItemId(null);
    setAddedItemId(item.id);
    setTimeout(() => setAddedItemId(null), 1500);
  };

  const data = specialData || getDefaultData();

  if (!data?.is_active || specialItems.length === 0) return null;

  if (loading) {
    return (
      <section className="special-section">
        <div className="special-container">
          <div className="special-loading">
            <div
              className="special-spinner"
              style={{ borderTopColor: primaryColor }}
            ></div>
            <p>Loading specials...</p>
          </div>
        </div>
      </section>
    );
  }

  const displayedItems = showAll ? specialItems : specialItems.slice(0, 3);
  const hasMoreItems   = specialItems.length > 3;

  return (
    <section className="special-section">
      {/* Decorative Leaf */}
      <div className="leaf-decoration leaf-bottom">
        <img src={leafImage} alt="" className="leaf-img" />
      </div>

      <div className="special-container">
        {/* Section Header */}
        <div className="special-header">
          <span className="special-label" style={{ color: primaryColor }}>
            {data.subheading}
          </span>
          <h2 className="special-title" style={{ color: primaryColor }}>
            {data.heading}
          </h2>
          {data.description && (
            <p className="special-subtitle">{data.description}</p>
          )}
        </div>

        {/* Items Grid */}
        <div className="special-grid">
          {displayedItems.map((item) => {
            const discount    = calculateDiscount(item.original_price, item.price);
            const isExpanded  = expandedDescriptions[item.id];
            const hasLongDesc = item.description && item.description.length > 100;
            const isAdding    = addingItemId === item.id;
            const isAdded     = addedItemId  === item.id;
            const dietary     = DIETARY_ICONS[item.dietary_type] || DIETARY_ICONS['non_vegetarian'];

            return (
              <div key={item.id} className="special-card">

                {/* ── Image ── */}
                <div className="special-card-image">
                  {/* Discount badge */}
                  {discount > 0 && (
                    <div
                      className="discount-badge"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {discount}% OFF
                    </div>
                  )}

                  {/* Dietary badge */}
                  <div className="dietary-badge" title={dietary.label}>
                    <img
                      src={dietary.icon}
                      alt={dietary.label}
                      className="dietary-badge-img"
                    />
                    <span className="dietary-badge-label">{dietary.label}</span>
                  </div>

                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="card-img"
                    />
                  ) : (
                    <div className="no-image-placeholder">
                      <span>No Image</span>
                    </div>
                  )}
                </div>

                {/* ── Content ── */}
                <div className="special-card-content">

                  {/* Name */}
                  <div className="card-header">
                    <h3 className="card-title" style={{ color: primaryColor }}>
                      {item.name}
                    </h3>
                  </div>

                  {/* Badges row: bestseller / new */}
                  {(item.is_bestseller || item.is_new) && (
                    <div className="item-tag-row">
                      {item.is_bestseller && (
                        <span className="item-tag tag-best">🔥 Best Seller</span>
                      )}
                      {item.is_new && (
                        <span className="item-tag tag-new">✨ New</span>
                      )}
                    </div>
                  )}

                  {/* Price */}
                  <div className="price-section">
                    <div className="price-row">
                      <span
                        className="current-price"
                        style={{ color: primaryColor }}
                      >
                        {formatPrice(item.price)}
                      </span>
                      {item.original_price && item.original_price > item.price && (
                        <span className="original-price">
                          {formatPrice(item.original_price)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {item.description && (
                    <div className="card-description-wrapper">
                      <p className={`card-description ${isExpanded ? 'expanded' : 'collapsed'}`}>
                        {item.description}
                      </p>
                      {hasLongDesc && (
                        <button
                          className="show-more-btn"
                          onClick={() => toggleDescription(item.id)}
                          style={{ color: primaryColor }}
                        >
                          {isExpanded ? (
                            <>Show Less <FaChevronUp style={{ marginLeft: '4px', fontSize: '12px' }} /></>
                          ) : (
                            <>Show More <FaChevronDown style={{ marginLeft: '4px', fontSize: '12px' }} /></>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Prep time */}
                  {item.preparation_time && (
                    <span className="special-prep-time">
                      ⏱ {item.preparation_time} min
                    </span>
                  )}

                  {/* Add to cart / Order Now button */}
                  {onAddToCart ? (
                    <button
                      className={`card-button ${isAdded ? 'btn-added' : ''}`}
                      style={{
                        borderColor:     isAdded ? '#10b981' : primaryColor,
                        color:           isAdded ? '#10b981' : primaryColor,
                        backgroundColor: isAdded
                          ? 'rgba(16,185,129,0.1)'
                          : 'transparent',
                      }}
                      onClick={(e) => handleAddToCart(e, item)}
                      disabled={isAdding}
                      onMouseEnter={(e) => {
                        if (!isAdded && !isAdding) {
                          e.currentTarget.style.backgroundColor = primaryColor;
                          e.currentTarget.style.color = '#fff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAdded && !isAdding) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = primaryColor;
                        }
                      }}
                    >
                      {isAdding ? (
                        <span className="btn-spinner" />
                      ) : isAdded ? (
                        <span>✓ Added</span>
                      ) : (
                        <>
                          <span>Order Now</span>
                          <FaArrowRight className="arrow-icon" />
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      className="card-button"
                      style={{ borderColor: primaryColor, color: primaryColor }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor;
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = primaryColor;
                      }}
                    >
                      <span>Order Now</span>
                      <FaArrowRight className="arrow-icon" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Show More / Less */}
        {hasMoreItems && (
          <div className="show-more-container">
            <button
              className="show-more-items-btn"
              onClick={() => setShowAll(!showAll)}
              style={{ borderColor: primaryColor, color: primaryColor }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = primaryColor;
              }}
            >
              {showAll ? (
                <>Show Less <FaChevronUp style={{ marginLeft: '8px' }} /></>
              ) : (
                <>View All {specialItems.length} Items <FaChevronDown style={{ marginLeft: '8px' }} /></>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TodaysSpecial;
